import { TextWithTooltip } from '@components/common/TextWithTooltip';
import { Box, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { CaretLeft } from '@phosphor-icons/react';
import useEntityMentionContext from '@providers/EntityMention/useEntityMentionContext';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHoverKeyboardNavigation } from '../hooks/useHoverKeyboardNavigation';
import { type EntitySuggestionItem, type EntityTypeMeta } from '../types';

export type EntitySuggestionListRef = {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

export interface EntitySuggestionListProps {
    /**
     * Entity suggestions to display
     */
    items: EntitySuggestionItem[];

    /**
     * Callback when entity is selected
     */
    command: (item: EntitySuggestionItem) => void;

    /**
     * Current query for context
     */
    query: string;

    /**
     * Selected entity type
     */
    selectedEntityType: string | null;

    /**
     * Callback to go back to entity type selection
     */
    onBack?: () => void;

    /**
     * Loading state
     */
    isLoading?: boolean;

    entityMeta: EntityTypeMeta;
}

/**
 * Component for displaying entity suggestions with pagination support
 */
export const EntitySuggestionList = forwardRef<
    EntitySuggestionListRef,
    EntitySuggestionListProps
>(function EntitySuggestionListInner(
    {
        items,
        command,
        query,
        selectedEntityType,
        onBack,
        isLoading = false,
        entityMeta,
    },
    ref,
) {
    const parentRef = useRef<HTMLDivElement>(null);

    const { loadMoreEntities, setSearchQuery } = useEntityMentionContext(
        (context) => context.actions,
    );

    const { entities } = useEntityMentionContext((context) => context.state);

    const prevLengthRef = useRef<number>(items.length);
    const prevTypeRef = useRef<string | null>(selectedEntityType);
    const prevQueryRef = useRef<string>(query);

    const {
        activeIndex,
        upHandler,
        downHandler,
        enterHandler,
        handleMouseEnter,
        handleMouseLeave,
        reset,
    } = useHoverKeyboardNavigation(items.length);

    const rowVirtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 40,
        overscan: 5,
    });

    const selectItem = (index: number) => {
        if (index >= items.length) {
            return;
        }

        const suggestion = items[index];
        if (suggestion.disabled) return;

        command(suggestion);
    };

    const backHandler = () => {
        if (onBack) {
            onBack();
            return true;
        }
        return false;
    };
    // Reset only when dataset changes (not when we append for infinite scroll)
    useEffect(() => {
        const isNewType = selectedEntityType !== prevTypeRef.current;
        const isNewQuery = query !== prevQueryRef.current;
        const shrank = items.length < prevLengthRef.current;
        const shouldReset = isNewType || isNewQuery || shrank;

        if (shouldReset) {
            reset();
            if (parentRef.current) {
                parentRef.current.scrollTop = 0;
            }
        }

        prevTypeRef.current = selectedEntityType;
        prevQueryRef.current = query;
        prevLengthRef.current = items.length;
    }, [items.length, reset, selectedEntityType, query]);

    // Scroll to selected item when activeIndex changes
    useEffect(() => {
        if (items.length > 0) {
            rowVirtualizer.scrollToIndex(activeIndex);
        }
    }, [activeIndex, rowVirtualizer, items.length]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
                const newIndex = upHandler();
                rowVirtualizer.scrollToIndex(newIndex);
                return true;
            }

            if (event.key === 'ArrowDown') {
                const newIndex = downHandler();
                rowVirtualizer.scrollToIndex(newIndex);
                return true;
            }

            if (event.key === 'Enter') {
                const index = enterHandler();
                if (items[index]?.disabled) return true;
                selectItem(index);
                return true;
            }

            if (event.key === 'Backspace' && query === '') {
                return backHandler();
            }

            if (event.key === 'Escape') {
                return backHandler();
            }

            if (event.key === 'ArrowLeft' && query === '') {
                return backHandler();
            }

            return false;
        },
    }));

    const isLoadingMore =
        selectedEntityType &&
        entities[selectedEntityType]?.isLoading &&
        items.length > 0;

    return (
        <Stack
            p={4}
            maw={400}
            w={250}
            className="gap-0"
            // Prevent the editor from losing focus when clicking inside the popup
            onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault();
            }}
        >
            {/* Header with back button + search */}
            <Stack className="gap-1 p-2">
                {selectedEntityType && (
                    <Group className="items-center gap-1">
                        {onBack && (
                            <Button
                                variant={ButtonVariant.UNSTYLED}
                                className="!h-auto p-0"
                                onClick={onBack}
                                onMouseDown={(
                                    event: React.MouseEvent<HTMLButtonElement>,
                                ) => {
                                    event.preventDefault();
                                }}
                            >
                                <CaretLeft size={12} weight="bold" />
                            </Button>
                        )}
                        <Text className="text-sm font-medium text-white/40">
                            {entityMeta[selectedEntityType]?.label ||
                                selectedEntityType}
                        </Text>
                    </Group>
                )}
            </Stack>

            {/* Loading state for initial load */}
            {isLoading && items.length === 0 ? (
                <Box p="md" className="flex items-center justify-center">
                    <Loader size="sm" color="gray" />
                </Box>
            ) : items.length > 0 ? (
                <Box
                    id="mention-scroll"
                    style={{ height: '300px', overflow: 'auto' }}
                    className="gap-2 p-0"
                >
                    {isLoading && (
                        <Box className="px-3 py-2">
                            <Loader size="xs" color="gray" />
                        </Box>
                    )}
                    <InfiniteScroll
                        dataLength={items.length}
                        next={() => {
                            if (selectedEntityType) {
                                void loadMoreEntities(selectedEntityType);
                            }
                        }}
                        hasMore={Boolean(
                            selectedEntityType &&
                                entities[selectedEntityType]?.pagination
                                    .hasMore,
                        )}
                        loader={
                            <Box p="sm" className="text-center">
                                <Text size="xs" color="dimmed">
                                    Loading more...
                                </Text>
                            </Box>
                        }
                        scrollableTarget="mention-scroll"
                        scrollThreshold="80%"
                        style={{ overflow: 'visible' }}
                    >
                        <Stack
                            ref={parentRef}
                            style={{
                                height: `${rowVirtualizer.getTotalSize()}px`,
                                width: '100%',
                                position: 'relative',
                            }}
                            className="gap-0"
                        >
                            {rowVirtualizer
                                .getVirtualItems()
                                .map((virtualRow) => {
                                    const item = items[virtualRow.index];
                                    const IconComponent = item.icon;
                                    const isActive =
                                        virtualRow.index === activeIndex;
                                    const {
                                        customRender: CustomRenderComponent,
                                    } = entityMeta[item.type] || {};

                                    return (
                                        <Box
                                            key={item.id}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: `36px`,
                                                transform: `translateY(${virtualRow.start}px)`,
                                            }}
                                        >
                                            <Box className="h-full w-full">
                                                <Button
                                                    onClick={() => {
                                                        if (item.disabled)
                                                            return;
                                                        selectItem(
                                                            virtualRow.index,
                                                        );
                                                    }}
                                                    onMouseEnter={() =>
                                                        handleMouseEnter(
                                                            virtualRow.index,
                                                        )
                                                    }
                                                    onMouseLeave={
                                                        handleMouseLeave
                                                    }
                                                    className="h-full w-full"
                                                    styles={(theme) => ({
                                                        root: {
                                                            padding: '6px 8px',
                                                            width: '100%',
                                                            fontSize:
                                                                theme.fontSizes
                                                                    .sm,
                                                            fontWeight: 500,
                                                            textAlign: 'left',
                                                            backgroundColor:
                                                                isActive
                                                                    ? 'rgb(var(--color-gray-840)) !important'
                                                                    : 'transparent',
                                                            color: 'rgb(var(--color-white))',
                                                            cursor: 'pointer',
                                                            minHeight: 36,
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgb(var(--color-gray-840)) !important',
                                                            },
                                                        },
                                                        inner: {
                                                            justifyContent:
                                                                'flex-start',
                                                            paddingLeft: 4,
                                                            paddingRight: 4,
                                                        },
                                                        label: {
                                                            justifyContent:
                                                                'flex-start',
                                                            padding: '0 !important',
                                                            width: '100%',
                                                        },
                                                    })}
                                                >
                                                    {CustomRenderComponent ? (
                                                        <CustomRenderComponent
                                                            item={item}
                                                        />
                                                    ) : (
                                                        <Box className="flex w-full items-center gap-1">
                                                            {IconComponent && (
                                                                <IconComponent
                                                                    size={14}
                                                                />
                                                            )}
                                                            <Box className="min-w-0 flex-1">
                                                                <TextWithTooltip
                                                                    text={
                                                                        item.label
                                                                    }
                                                                    className="z-[9999] !m-0 truncate rounded text-sm font-medium text-white"
                                                                    style={{
                                                                        display:
                                                                            'block',
                                                                        overflow:
                                                                            'hidden',
                                                                        textOverflow:
                                                                            'ellipsis',
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                    }}
                                                                    tooltipProps={{
                                                                        offset: 6,
                                                                        position:
                                                                            'top',
                                                                        withArrow: true,
                                                                        arrowSize: 10,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                })}
                        </Stack>
                    </InfiniteScroll>
                </Box>
            ) : (
                <Box className="px-3 py-2">
                    <Text className="text-xs text-white/40">
                        Nothing found.
                    </Text>
                </Box>
            )}
        </Stack>
    );
});

EntitySuggestionList.displayName = 'EntitySuggestionList';
